import React from 'react';

import type {
	PricingConfiguration,
	SurchargeRatesConfig,
} from '../../../../types';

interface SurchargeSectionProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
}

const FIELDS: Array<{
	key: keyof SurchargeRatesConfig;
	label: string;
	help: string;
	unit: '%' | '$';
	max?: number;
}> = [
	{
		key: 'surcharge_rate',
		label: 'Surcharge rate',
		help: 'Paid by the cardholder on credit sales only, never on debit or prepaid. Card brands cap it at 3%.',
		unit: '%',
		max: 3,
	},
	{
		key: 'merchant_rate',
		label: 'Merchant rate on surcharged volume',
		help: 'All-in rate on the sale plus the surcharge. Interchange and assessments are included.',
		unit: '%',
	},
	{
		key: 'non_surcharged_rate',
		label: 'Rate on volume that is not surcharged',
		help: 'Signature and PIN debit, prepaid, EBT, fleet and unknown cards.',
		unit: '%',
	},
	{
		key: 'authorization_fee',
		label: 'Authorization fee',
		help: 'Per transaction, on every card type.',
		unit: '$',
	},
];

// Card rules cap the surcharge at the cost of acceptance: m >= s / (1 + s).
export const breakEvenMerchantRate = (surchargeRate: number) =>
	Math.round((surchargeRate / (100 + surchargeRate)) * 100 * 10000) / 10000;

export const isSurchargeComplete = (rates?: Partial<SurchargeRatesConfig>) => {
	const { surcharge_rate: s, merchant_rate: m } = rates ?? {};
	return (
		FIELDS.every(({ key }) => Number.isFinite(rates?.[key])) &&
		s !== undefined &&
		m !== undefined &&
		s <= 3 &&
		m >= breakEvenMerchantRate(s)
	);
};

export const SurchargeSection: React.FC<SurchargeSectionProps> = ({
	configuration,
	onUpdate,
}) => {
	const rates: Partial<SurchargeRatesConfig> =
		configuration.surcharge_rates || {};
	const surcharge = rates.surcharge_rate;
	const breakEven =
		surcharge !== undefined && Number.isFinite(surcharge)
			? breakEvenMerchantRate(surcharge)
			: undefined;

	const update = (key: keyof SurchargeRatesConfig, value: string) => {
		onUpdate({
			surcharge_rates: {
				...rates,
				[key]: value === '' ? undefined : Number(value),
			} as SurchargeRatesConfig,
		});
	};

	return (
		<div className="space-y-4">
			{FIELDS.map(({ key, label, help, unit, max }) => (
				<div key={key} className="p-4 bg-gray-50 rounded-lg">
					<label
						htmlFor={`surcharge_${key}`}
						className="block text-sm font-medium text-gray-700"
					>
						{label}
					</label>
					<div className="mt-2 flex items-center gap-2">
						{unit === '$' && <span className="text-gray-600">$</span>}
						<input
							id={`surcharge_${key}`}
							type="number"
							min={0}
							max={max}
							step="0.001"
							value={rates[key] ?? ''}
							onChange={(e) => update(key, e.target.value)}
							className="w-32 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
						/>
						{unit === '%' && <span className="text-gray-600">%</span>}
					</div>
					<p className="mt-1 text-sm text-gray-600">{help}</p>
				</div>
			))}
			{breakEven !== undefined && (
				<div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
					<p className="text-sm text-blue-900">
						At a {surcharge}% surcharge the merchant rate must be at least{' '}
						{breakEven.toFixed(4)}% (card rules cap a surcharge at the cost of
						acceptance). At that rate the merchant pays $0 on surcharged volume.
					</p>
				</div>
			)}
		</div>
	);
};
